import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCheck,
  Clock,
  CornerUpLeft,
  Download,
  ExternalLink,
  FileText,
  Headphones,
  List,
  Loader2,
  Paperclip,
  Play,
  Send,
  Workflow,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { parseRichMessage } from './richMessage';
import {
  getInboxMessages,
  markInboxConversationRead,
  sendInboxMedia,
  sendInboxMessage,
  sendWhatsAppFlow,
  sendWhatsAppTemplateToLeads
} from '../../services/api/implementation/impl';
import {
  MESSAGE_LIMIT,
  addOptimisticMedia,
  addOptimisticMessage,
  clearUnreadInCache,
  markOptimisticFailed,
  messagesQueryKey,
  reconcileOptimisticMessage,
  updateListPreview
} from '../../utils/whatsappInboxCache';
import SendWhatsAppPicker from './SendWhatsAppPicker';
import InboxPreleadDetailModal from './InboxPreleadDetailModal';

function getInitials(name, phone) {
  const source = String(name || '').trim(); // contact initials
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits) return digits.slice(-2);
  return '?';
}

function formatMessageTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const isSameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (isSameDay) return time;
  const day = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${day}, ${time}`;
}

function isSessionWindowError(message) {
  return /24\s*-?\s*h(ou)?r|session|messaging window|window has|hasn'?t messaged|re-?engage|outside .*window|customer .*message/i.test(
    String(message || '')
  );
}

const MAX_MEDIA_BYTES = 25 * 1024 * 1024; // 25 MB — matches backend limit.

const MEDIA_ACCEPT = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv';

const MEDIA_PREVIEW_LABELS = {
  document: '📄 Document',
  image: '🖼️ Photo',
  video: '🎥 Video',
  audio: '🎧 Audio'
};

const MEDIA_ERROR_MESSAGES = {
  MEDIA_FILE_REQUIRED: 'Please choose a file to send.',
  INVALID_MEDIA_TYPE: 'That file type isn’t supported.',
  MEDIA_TOO_LARGE: 'File is too large. Maximum size is 25 MB.',
  INVALID_PHONE: 'This conversation’s phone number is invalid.',
  MEDIA_S3_UPLOAD_FAILED: 'Upload failed. Please try again.',
  WHATSAPP_INTEGRATION_MISSING: 'WhatsApp isn’t connected for your account.',
  VENDOR_PHONE_MISSING: 'Your WhatsApp sender number isn’t configured.'
};

// Infer the media category the backend expects from the file's MIME type.
function inferMediaType(file) {
  const mime = String(file?.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'document';
}

function mapMediaError(err) {
  const raw = err?.response?.data?.message || '';
  if (MEDIA_ERROR_MESSAGES[raw]) return MEDIA_ERROR_MESSAGES[raw];
  if (isSessionWindowError(raw)) {
    return 'You can only send media within 24 hours of the customer’s last message.';
  }
  return raw || 'Couldn’t send media. Please try again.';
}

function resolveFlowApprovalStatus(flow) {
  return String(
    flow?.template_approval_status ?? flow?.template_status ?? flow?.approval_status ?? ''
  ).toUpperCase();
}

// A flow can only be sent once its opener template is approved by Meta.
function isFlowSendable(flow) {
  const status = resolveFlowApprovalStatus(flow);
  return status === 'APPROVED' || status === 'ACTIVE';
}

// Long lists (e.g. ten time slots) stay collapsed until asked for.
const COLLAPSED_OPTION_COUNT = 3;

function formatFileExtension(filename, url) {
  const source = String(filename || url || '');
  const match = source.split('?')[0].match(/\.([a-z0-9]{1,5})$/i);
  return match ? match[1].toUpperCase() : 'FILE';
}

const OutgoingStatus = ({ status, tone = 'light' }) => {
  const isDark = tone === 'dark';
  const clockClass = isDark ? 'text-gray-400' : 'text-white/70';
  const failedClass = isDark ? 'text-red-500' : 'text-red-100';
  const readClass = isDark ? 'text-[#00a5f4]' : 'text-white/80';
  const sentClass = isDark ? 'text-gray-400' : 'text-white/80';

  if (status === 'sending') {
    return <Clock className={`h-3 w-3 ${clockClass}`} strokeWidth={2.5} />;
  }
  if (status === 'failed') {
    return <span className={`text-[10px] font-medium ${failedClass}`}>Failed</span>;
  }
  if (status === 'read' || status === 'delivered') {
    return <CheckCheck className={`h-3.5 w-3.5 ${readClass}`} strokeWidth={2.5} />;
  }
  return <Check className={`h-3.5 w-3.5 ${sentClass}`} strokeWidth={2.5} />;
};

const MediaMessage = ({ content, time, status, isOutgoing }) => {
  const { mediaType, url, caption, filename } = content;
  const bubbleClass = isOutgoing
    ? 'rounded-br-sm bg-primary text-white'
    : 'rounded-bl-sm bg-white text-gray-800';
  const timeClass = isOutgoing ? 'text-white/70' : 'text-gray-400';

  const meta = (
    <div className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${timeClass}`}>
      <span>{time}</span>
      {isOutgoing ? <OutgoingStatus status={status} /> : null}
    </div>
  );

  // Image → show inline preview.
  if (mediaType === 'image' && url) {
    return (
      <div className={`max-w-[80%] overflow-hidden rounded-2xl shadow-sm ${bubbleClass}`}>
        <a href={url} target="_blank" rel="noopener noreferrer" className="block">
          <img src={url} alt={caption || filename || 'image'} className="max-h-80 w-full object-cover" />
        </a>
        <div className="px-3 py-2 text-sm">
          {caption ? <p className="whitespace-pre-wrap break-words">{caption}</p> : null}
          {meta}
        </div>
      </div>
    );
  }

  // Video → native player.
  if (mediaType === 'video' && url) {
    return (
      <div className={`max-w-[80%] overflow-hidden rounded-2xl shadow-sm ${bubbleClass}`}>
        <video src={url} controls className="max-h-80 w-full bg-black object-contain" />
        <div className="px-3 py-2 text-sm">
          {caption ? <p className="whitespace-pre-wrap break-words">{caption}</p> : null}
          {meta}
        </div>
      </div>
    );
  }

  // Audio → native player.
  if (mediaType === 'audio' && url) {
    return (
      <div className={`max-w-[80%] rounded-2xl px-3 py-2.5 shadow-sm ${bubbleClass}`}>
        <div className="mb-1 flex items-center gap-2 text-sm">
          <Headphones className="h-4 w-4 shrink-0" strokeWidth={2.25} />
          <span className="truncate">{filename || 'Audio message'}</span>
        </div>
        <audio src={url} controls className="w-56 max-w-full" />
        {caption ? <p className="mt-1 whitespace-pre-wrap break-words text-sm">{caption}</p> : null}
        {meta}
      </div>
    );
  }

  // Document / fallback → file card with download link.
  const ext = formatFileExtension(filename, url);
  const iconTint = isOutgoing ? 'bg-white/15 text-white' : 'bg-primary/10 text-primary';
  const subTint = isOutgoing ? 'text-white/70' : 'text-gray-400';

  return (
    <div className={`max-w-[80%] rounded-2xl px-3 py-2.5 text-sm shadow-sm ${bubbleClass}`}>
      <a
        href={url || undefined}
        target="_blank"
        rel="noopener noreferrer"
        download={filename || undefined}
        className="flex items-center gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconTint}`}>
          {mediaType === 'video' ? (
            <Play className="h-5 w-5" strokeWidth={2} />
          ) : mediaType === 'audio' ? (
            <Headphones className="h-5 w-5" strokeWidth={2} />
          ) : (
            <FileText className="h-5 w-5" strokeWidth={2} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{filename || 'Attachment'}</p>
          <p className={`text-[11px] ${subTint}`}>{ext} document</p>
        </div>
        {url ? <Download className={`h-4 w-4 shrink-0 ${subTint}`} strokeWidth={2} /> : null}
      </a>
      {caption ? <p className="mt-2 whitespace-pre-wrap break-words">{caption}</p> : null}
      {meta}
    </div>
  );
};

const TemplateMessage = ({ content, time, status, isOutgoing }) => {
  const { name, variables, mediaUrl } = content;
  const bubbleClass = isOutgoing
    ? 'rounded-br-sm bg-primary text-white'
    : 'rounded-bl-sm bg-white text-gray-800';
  const labelClass = isOutgoing ? 'text-white/70' : 'text-gray-400';
  const varClass = isOutgoing ? 'text-white/80' : 'text-gray-600';
  const timeClass = isOutgoing ? 'text-white/70' : 'text-gray-400';

  return (
    <div className={`max-w-[80%] overflow-hidden rounded-2xl shadow-sm ${bubbleClass}`}>
      {mediaUrl ? (
        <div className="aspect-video w-full bg-black/10">
          <img src={mediaUrl} alt="" className="h-full w-full object-cover" />
        </div>
      ) : null}
      <div className="px-3.5 py-2.5 text-sm">
        <div className={`mb-1 flex items-center gap-1.5 text-[11px] font-medium ${labelClass}`}>
          <FileText className="h-3.5 w-3.5" strokeWidth={2.25} />
          <span>Template message</span>
        </div>
        <p className="break-words font-semibold">{name}</p>
        {variables.length ? (
          <p className={`mt-1 break-words text-xs ${varClass}`}>{variables.join(' • ')}</p>
        ) : null}
        <div className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${timeClass}`}>
          <span>{time}</span>
          {isOutgoing ? <OutgoingStatus status={status} /> : null}
        </div>
      </div>
    </div>
  );
};

const InteractiveMessage = ({ content, time, status, isOutgoing }) => {
  const { header, body, footer, buttons, options = [], isList, isLink } = content;
  const [showAllOptions, setShowAllOptions] = useState(false);
  const ButtonIcon = isList ? List : isLink ? ExternalLink : CornerUpLeft;
  const visibleOptions = showAllOptions ? options : options.slice(0, COLLAPSED_OPTION_COUNT);
  const hiddenCount = options.length - visibleOptions.length;

  return (
    <div className="w-[280px] max-w-[80vw] overflow-hidden rounded-2xl bg-white text-gray-800 shadow-sm ring-1 ring-black/5">
      <div className="px-3.5 pt-2.5 pb-2">
        {header ? (
          <p className="mb-1 whitespace-pre-wrap break-words text-sm font-semibold text-gray-900">
            {header}
          </p>
        ) : null}
        {body ? (
          <p className="whitespace-pre-wrap break-words text-sm text-gray-800">{body}</p>
        ) : null}
        {footer ? (
          <p className="mt-1 whitespace-pre-wrap break-words text-xs text-gray-400">{footer}</p>
        ) : null}
        <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-gray-400">
          <span>{time}</span>
          {isOutgoing ? <OutgoingStatus status={status} tone="dark" /> : null}
        </div>
      </div>

      {buttons.length ? (
        <div className="flex flex-col border-t border-gray-100">
          {buttons.map((button, index) => {
            const className = `flex items-center justify-center gap-2 px-3 py-2.5 text-center text-sm font-medium text-[#00a5f4] ${
              index > 0 ? 'border-t border-gray-100' : ''
            }`;
            const inner = (
              <>
                <ButtonIcon className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                <span className="truncate">{button.label}</span>
              </>
            );
            return button.url ? (
              <a
                key={`${button.label}-${index}`}
                href={button.url}
                target="_blank"
                rel="noreferrer"
                className={`${className} hover:bg-gray-50`}>
                {inner}
              </a>
            ) : (
              <div key={`${button.label}-${index}`} className={className}>
                {inner}
              </div>
            );
          })}
        </div>
      ) : null}

      {/* What the lead was offered, like the sheet WhatsApp opens from the list button. */}
      {options.length ? (
        <div className="border-t border-gray-100 bg-gray-50/70 px-3.5 py-2">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {options.length} option{options.length === 1 ? '' : 's'} offered
          </p>
          <ul className="space-y-1">
            {visibleOptions.map((option, index) => (
              <li key={`${option.label}-${index}`} className="text-xs text-gray-700">
                <span className="font-medium">{option.label}</span>
                {option.description ? (
                  <span className="text-gray-400"> · {option.description}</span>
                ) : null}
              </li>
            ))}
          </ul>
          {options.length > COLLAPSED_OPTION_COUNT ? (
            <button
              type="button"
              onClick={() => setShowAllOptions((value) => !value)}
              className="mt-1 text-xs font-medium text-[#00a5f4] hover:underline">
              {showAllOptions ? 'Show less' : `Show ${hiddenCount} more`}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const MessageSkeleton = () => (
  <div className="space-y-4 px-6 py-6">
    {[35, 55, 40, 60, 30].map((width, index) => (
      <div
        key={index}
        className={`flex ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
        <div
          className="h-9 animate-pulse rounded-2xl bg-gray-200"
          style={{ width: `${width}%` }}
        />
      </div>
    ))}
  </div>
);

const WhatsAppMessageThread = ({ conversation, leadId, projectId, onBack }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const phone = conversation?.phone_number;
  const contactName = conversation?.contact_name;
  const linkedLeadId = Number(conversation?.lead_id || leadId) || null;
  const linkedPreLeadId = Number(conversation?.pre_lead_id) || null;
  const linkedPreLeadListId = Number(conversation?.pre_lead_list_id) || null;
  const resolvedLeadId = (() => {
    const raw = leadId ?? conversation?.lead_id ?? conversation?.leadId ?? null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  })();
  const resolvedPreLeadId = (() => {
    const raw = conversation?.pre_lead_id ?? conversation?.preLeadId ?? null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  })();

  const [draft, setDraft] = useState('');
  const [sessionBlocked, setSessionBlocked] = useState(false);
  const [pendingMedia, setPendingMedia] = useState(null); // { file, type, previewUrl }
  const [flowPickerOpen, setFlowPickerOpen] = useState(false);
  const [preleadModalOpen, setPreleadModalOpen] = useState(false);

  const canSendFlow = Boolean(resolvedLeadId && projectId);

  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const prevScrollHeightRef = useRef(null);
  const scrollBottomPendingRef = useRef(false);
  const nearBottomRef = useRef(true);
  const prevMsgCountRef = useRef(0);

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: messagesQueryKey(phone),
    enabled: Boolean(phone),
    queryFn: ({ pageParam = 1 }) =>
      getInboxMessages(phone, { page: pageParam, limit: MESSAGE_LIMIT }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.pagination;
      if (!pagination) return undefined;
      const loaded = pagination.page * pagination.limit;
      return loaded < pagination.total ? pagination.page + 1 : undefined;
    }
  });

  // API returns each page oldest -> newest, with page 1 being the newest batch.
  // Reverse the page order (older pages on top); keep each page's items as-is.
  const messages = useMemo(
    () =>
      (data?.pages || [])
        .slice()
        .reverse()
        .flatMap((page) => page?.messages || []),
    [data]
  );

  useEffect(() => {
    setDraft('');
    setSessionBlocked(false);
    setPendingMedia((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
    setFlowPickerOpen(false);
    setPreleadModalOpen(false);
    scrollBottomPendingRef.current = true;
    nearBottomRef.current = true;
    prevMsgCountRef.current = 0;
  }, [phone]);

  // Release any lingering object URL when the thread unmounts.
  useEffect(
    () => () => {
      setPendingMedia((prev) => {
        if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
        return null;
      });
    },
    []
  );

  // Mark read on open: clear the badge locally, then confirm with the API.
  useEffect(() => {
    if (!phone) return;
    clearUnreadInCache(queryClient, phone);
    markInboxConversationRead(phone).catch(() => {});
  }, [phone, queryClient]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const grew = messages.length > prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;

    if (prevScrollHeightRef.current != null) {
      // Older messages were prepended — keep the viewport anchored.
      el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = null;
    } else if (scrollBottomPendingRef.current && messages.length > 0) {
      // Initial open / explicit send.
      el.scrollTop = el.scrollHeight;
      scrollBottomPendingRef.current = false;
    } else if (grew && nearBottomRef.current) {
      // New incoming message while already near the bottom.
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, phone]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (el.scrollTop < 60 && hasNextPage && !isFetchingNextPage) {
      prevScrollHeightRef.current = el.scrollHeight;
      fetchNextPage();
    }
  };

  const sendMutation = useMutation({
    mutationFn: (text) => sendInboxMessage(phone, text)
  });

  const handleSend = () => {
    const text = draft.trim();
    if (!text || sessionBlocked || sendMutation.isPending) return;

    const tempId = `temp-${Date.now()}`;
    addOptimisticMessage(queryClient, phone, tempId, text);
    setDraft('');
    nearBottomRef.current = true;
    scrollBottomPendingRef.current = true;

    sendMutation.mutate(text, {
      onSuccess: (result) => {
        const saved = result?.message;
        if (saved) reconcileOptimisticMessage(queryClient, phone, tempId, saved);
        updateListPreview(queryClient, phone, text);
      },
      onError: (err) => {
        markOptimisticFailed(queryClient, phone, tempId);
        const message = err?.response?.data?.message || 'Message failed to send.';
        if (isSessionWindowError(message)) setSessionBlocked(true);
        toast.error(message);
      }
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const mediaMutation = useMutation({
    mutationFn: ({ file, type }) => sendInboxMedia(phone, { file, type })
  });

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    // Reset the input so selecting the same file again re-triggers onChange.
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_MEDIA_BYTES) {
      toast.error('File is too large. Maximum size is 25 MB.');
      return;
    }
    setPendingMedia((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      const type = inferMediaType(file);
      return {
        file,
        type,
        previewUrl: type === 'image' || type === 'video' ? URL.createObjectURL(file) : null
      };
    });
  };

  const clearPendingMedia = () => {
    setPendingMedia((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  };

  const handleSendMedia = () => {
    if (!pendingMedia || sessionBlocked || mediaMutation.isPending) return;
    const { file, type } = pendingMedia;
    const tempId = `temp-${Date.now()}`;
    const localUrl = URL.createObjectURL(file);

    addOptimisticMedia(queryClient, phone, tempId, {
      file,
      type,
      localUrl
    });
    nearBottomRef.current = true;
    scrollBottomPendingRef.current = true;

    mediaMutation.mutate(
      { file, type },
      {
        onSuccess: (result) => {
          const saved = result?.message;
          if (saved) reconcileOptimisticMessage(queryClient, phone, tempId, saved);
          updateListPreview(queryClient, phone, MEDIA_PREVIEW_LABELS[type] || 'Media');
          URL.revokeObjectURL(localUrl);
          clearPendingMedia();
        },
        onError: (err) => {
          markOptimisticFailed(queryClient, phone, tempId);
          URL.revokeObjectURL(localUrl);
          const message = mapMediaError(err);
          if (isSessionWindowError(err?.response?.data?.message)) setSessionBlocked(true);
          toast.error(message);
        }
      }
    );
  };

  const flowMutation = useMutation({
    mutationFn: (flowId) =>
      sendWhatsAppFlow(flowId, {
        recipient_type: 'lead',
        lead_ids: [resolvedLeadId],
        variables: {},
        skip_template_if_session_open: false
      })
  });

  const templateMutation = useMutation({
    mutationFn: ({ templateName }) =>
      sendWhatsAppTemplateToLeads({
        projectId,
        leadIds: [leadId],
        templateName
      })
  });

  const waPickerBusy = flowMutation.isPending || templateMutation.isPending;

  const afterOutboundSuccess = (label) => {
    toast.success(label);
    setFlowPickerOpen(false);
    setSessionBlocked(false);
    nearBottomRef.current = true;
    scrollBottomPendingRef.current = true;
    queryClient.invalidateQueries({ queryKey: messagesQueryKey(phone) });
  };

  const handleSendFlow = (flow) => {
    if (!canSendFlow || waPickerBusy) return;
    flowMutation.mutate(flow.id, {
      onSuccess: () => afterOutboundSuccess(`“${flow.name || 'Flow'}” sent.`),
      onError: (err) => {
        const message = err?.response?.data?.message || 'Could not send flow. Please try again.';
        toast.error(message);
      }
    });
  };

  const handleSendTemplate = ({ templateName }) => {
    if (!canSendFlow || waPickerBusy) return;
    templateMutation.mutate(
      { templateName },
      {
        onSuccess: (data) => {
          const results = Array.isArray(data?.results) ? data.results : [];
          const failed = results.find((r) => r && r.ok === false);
          if (failed || data?.failCount > 0) {
            toast.error(
              failed?.error || data?.message || 'Template send failed. Check Pinggo/Meta template variables.'
            );
            queryClient.invalidateQueries({ queryKey: messagesQueryKey(phone) });
            return;
          }
          afterOutboundSuccess(`“${templateName}” template sent.`);
        },
        onError: (err) => {
          const message =
            err?.response?.data?.message || 'Could not send template. Please try again.';
          toast.error(message);
          queryClient.invalidateQueries({ queryKey: messagesQueryKey(phone) });
        }
      }
    );
  };

  const displayName = contactName || phone;
  const canOpenSource = Boolean(linkedLeadId || linkedPreLeadListId);

  const openContactSource = useCallback(() => {
    if (linkedLeadId) {
      navigate(`/builder/sales/${linkedLeadId}`);
      return;
    }
    if (linkedPreLeadListId) {
      try {
        localStorage.setItem('salesMainSecondaryTab', 'Prelead List');
      } catch {
        /* ignore */
      }
      navigate(`/builder/sales/prelead-lists/${linkedPreLeadListId}`, {
        state: {
          highlightPreLeadId: linkedPreLeadId || undefined,
          fromInbox: true
        }
      });
      return;
    }
    toast.error('No linked lead or prelead for this conversation yet.');
  }, [linkedLeadId, linkedPreLeadId, linkedPreLeadListId, navigate]);

  return (
    <div className="relative flex h-full min-h-0 flex-1 flex-col bg-[#F8F9FA]">
      <div className="flex shrink-0 items-center gap-3 border-b border-gray-100 bg-white px-4 py-3">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="rounded-full p-1 text-gray-500 transition-colors hover:bg-gray-100 md:hidden"
            aria-label="Back to conversations">
            <ArrowLeft className="h-5 w-5" />
          </button>
        ) : null}
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
          {getInitials(contactName, phone)}
        </div>
        <button
          type="button"
          onClick={openContactSource}
          disabled={!canOpenSource}
          title={
            canOpenSource
              ? linkedLeadId
                ? 'Open lead'
                : 'Open prelead list'
              : 'No linked lead or prelead'
          }
          className={`min-w-0 flex-1 text-left ${
            canOpenSource
              ? 'group cursor-pointer rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
              : 'cursor-default'
          }`}>
          <p
            className={`truncate text-sm font-bold text-gray-900 ${
              canOpenSource ? 'group-hover:text-primary group-hover:underline' : ''
            }`}>
            {displayName}
            {canOpenSource ? (
              <ExternalLink className="ml-1.5 inline h-3.5 w-3.5 align-[-2px] text-gray-400 group-hover:text-primary" />
            ) : null}
          </p>
          {contactName ? (
            <p
              className={`truncate text-xs text-gray-500 ${
                canOpenSource ? 'group-hover:text-primary/80 group-hover:underline' : ''
              }`}>
              +{phone}
            </p>
          ) : null}
        </button>
        {resolvedLeadId ? (
          <button
            type="button"
            onClick={() => navigate(`/builder/sales/${resolvedLeadId}`)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-customBlue/30 bg-[#E9F5F6] px-2.5 py-1.5 text-xs font-semibold text-customBlue transition-colors hover:bg-customBlue/20"
            title="Open lead">
            <ExternalLink className="h-3.5 w-3.5" />
            Open lead
          </button>
        ) : resolvedPreLeadId ? (
          <button
            type="button"
            onClick={() => setPreleadModalOpen(true)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-customBlue/30 bg-[#E9F5F6] px-2.5 py-1.5 text-xs font-semibold text-customBlue transition-colors hover:bg-customBlue/20"
            title="Open prelead">
            <ExternalLink className="h-3.5 w-3.5" />
            Open prelead
          </button>
        ) : null}
      </div>

      <div ref={scrollRef} onScroll={handleScroll} className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <MessageSkeleton />
        ) : isError ? (
          <div className="flex h-full flex-col items-center justify-center px-8 text-center text-gray-500">
            <AlertCircle className="mb-3 h-10 w-10 text-red-300" />
            <p className="text-sm font-medium text-gray-700">Couldn’t load messages</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary/90">
              Try again
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2 px-4 py-5 md:px-6">
            {isFetchingNextPage ? (
              <div className="flex justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              </div>
            ) : null}

            {messages.map((message) => {
              const isOutgoing = message.direction === 'outgoing';
              const key = message._tempId || message.id;
              const rich = parseRichMessage(message);

              return (
                <div key={key} className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
                  {rich?.kind === 'template' ? (
                    <TemplateMessage
                      content={rich}
                      time={formatMessageTime(message.created_at)}
                      status={message.status}
                      isOutgoing={isOutgoing}
                    />
                  ) : rich?.kind === 'media' ? (
                    <MediaMessage
                      content={rich}
                      time={formatMessageTime(message.created_at)}
                      status={message.status}
                      isOutgoing={isOutgoing}
                    />
                  ) : rich?.kind === 'interactive' ? (
                    <InteractiveMessage
                      content={rich}
                      time={formatMessageTime(message.created_at)}
                      status={message.status}
                      isOutgoing={isOutgoing}
                    />
                  ) : (
                    <div
                      className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                        isOutgoing
                          ? 'rounded-br-sm bg-primary text-white'
                          : 'rounded-bl-sm bg-white text-gray-800'
                      }`}>
                      {message.message_text ? (
                        <p className="whitespace-pre-wrap break-words">{message.message_text}</p>
                      ) : (
                        <p className="italic opacity-70">
                          {message.message_type ? `[${message.message_type}]` : '[message]'}
                        </p>
                      )}
                      <div
                        className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${
                          isOutgoing ? 'text-white/70' : 'text-gray-400'
                        }`}>
                        <span>{formatMessageTime(message.created_at)}</span>
                        {isOutgoing ? <OutgoingStatus status={message.status} /> : null}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {sessionBlocked ? (
        <div className="shrink-0 border-t border-gray-100 bg-amber-50 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-amber-800">
              <span className="font-semibold">Free-form replies are paused.</span> The 24-hour
              messaging window has closed, so you can only reach this customer with an approved
              template message until they reply.
            </p>
            {canSendFlow ? (
              <button
                type="button"
                onClick={() => setFlowPickerOpen(true)}
                className="flex shrink-0 items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100">
                <Workflow className="h-3.5 w-3.5" strokeWidth={2.25} />
                Send WhatsApp
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="shrink-0 border-t border-gray-100 bg-white">
          <input
            ref={fileInputRef}
            type="file"
            accept={MEDIA_ACCEPT}
            onChange={handleFileSelect}
            className="hidden"
          />

          {pendingMedia ? (
            <div className="border-b border-gray-100 px-3 pt-3">
              <div className="flex items-start gap-3 rounded-xl bg-gray-50 p-3">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100">
                  {pendingMedia.type === 'image' && pendingMedia.previewUrl ? (
                    <img
                      src={pendingMedia.previewUrl}
                      alt={pendingMedia.file.name}
                      className="h-full w-full object-cover"
                    />
                  ) : pendingMedia.type === 'video' && pendingMedia.previewUrl ? (
                    <video src={pendingMedia.previewUrl} className="h-full w-full object-cover" />
                  ) : pendingMedia.type === 'audio' ? (
                    <Headphones className="h-6 w-6 text-primary" strokeWidth={2} />
                  ) : (
                    <FileText className="h-6 w-6 text-primary" strokeWidth={2} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-800">
                    {pendingMedia.file.name}
                  </p>
                  <p className="text-[11px] text-gray-400">
                    {MEDIA_PREVIEW_LABELS[pendingMedia.type]} ·{' '}
                    {(pendingMedia.file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={clearPendingMedia}
                  disabled={mediaMutation.isPending}
                  className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 disabled:opacity-40"
                  aria-label="Remove attachment">
                  <X className="h-4 w-4" strokeWidth={2.25} />
                </button>
              </div>
            </div>
          ) : null}

          <div className="flex items-end gap-2 px-3 py-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={mediaMutation.isPending}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Attach a file">
              <Paperclip className="h-5 w-5" strokeWidth={2} />
            </button>

            {canSendFlow && !pendingMedia ? (
              <button
                type="button"
                onClick={() => setFlowPickerOpen(true)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                aria-label="Send WhatsApp"
                title="Send WhatsApp">
                <Workflow className="h-5 w-5" strokeWidth={2} />
              </button>
            ) : null}

            {pendingMedia ? (
              <>
                <div className="flex-1 self-center px-1 text-sm text-gray-500">
                  {mediaMutation.isPending ? 'Sending attachment…' : 'Attachment ready to send'}
                </div>
                <button
                  type="button"
                  onClick={handleSendMedia}
                  disabled={mediaMutation.isPending}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Send attachment">
                  {mediaMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" strokeWidth={2.25} />
                  )}
                </button>
              </>
            ) : (
              <>
                <TextareaAutosize
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message…"
                  minRows={1}
                  maxRows={5}
                  className="flex-1 resize-none rounded-2xl border border-gray-200 px-4 py-2.5 text-sm text-gray-800 outline-none focus:border-primary/60 placeholder:text-gray-400"
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!draft.trim() || sendMutation.isPending}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Send message">
                  {sendMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" strokeWidth={2.25} />
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {flowPickerOpen ? (
        <SendWhatsAppPicker
          projectId={projectId}
          contactName={displayName}
          isSending={waPickerBusy}
          sendingKey={
            templateMutation.isPending && templateMutation.variables?.templateName
              ? `template:${templateMutation.variables.templateName}`
              : flowMutation.isPending && flowMutation.variables != null
                ? `flow:${flowMutation.variables}`
                : null
          }
          onClose={() => {
            if (!waPickerBusy) setFlowPickerOpen(false);
          }}
          onSelectFlow={handleSendFlow}
          onSelectTemplate={handleSendTemplate}
        />
      ) : null}

      {preleadModalOpen && resolvedPreLeadId ? (
        <InboxPreleadDetailModal
          preLeadId={resolvedPreLeadId}
          contactName={contactName}
          phoneNumber={phone}
          onClose={() => setPreleadModalOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default WhatsAppMessageThread;
